import React, { useState } from 'react';
import { Plus, ChevronDown, ChevronUp, Package } from 'lucide-react';
import { getPersonInitials } from '../../utils/personInitials';

interface BookingResourceListProps {
  resources: any[];
  onRemoveResource?: (resourceId: string) => Promise<void>;
  onAddResource?: () => void;
  selectedResourceIds?: Set<string>;
  assets?: any[];
  onRemoveAsset?: (assetId: string) => Promise<void>;
  onAddAsset?: () => void;
}

export default function BookingResourceList({
  resources,
  onRemoveResource,
  onAddResource,
  selectedResourceIds = new Set(),
  assets = [],
  onRemoveAsset,
  onAddAsset
}: BookingResourceListProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const getResourceAvatar = (resource: any) => {
    const initials = getPersonInitials(resource.resource?.first_name, resource.resource?.last_name, '??');
    
    if (resource.resource?.avatar_url) {
      return (
        <img
          src={resource.resource.avatar_url}
          alt={`${resource.resource?.first_name} ${resource.resource?.last_name}`}
          className="w-full h-full object-cover"
        />
      );
    }
    return <span className="text-xs font-bold text-white">{initials}</span>;
  };

  const getAssetInitials = (asset: any) => {
    const name = asset.asset?.name || 'A';
    return name.split(/\s+/).map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const totalCount = resources.length + assets.length;

  if (!isExpanded) {
    return (
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setIsExpanded(true)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {totalCount > 0 ? (
              <div className="flex -space-x-2">
                {resources.slice(0, 5).map((resource, index) => (
                  <div 
                    key={resource.id}
                    className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 border-2 border-white flex items-center justify-center overflow-hidden relative z-10"
                    style={{ zIndex: 5 - index }}
                  >
                    {getResourceAvatar(resource)}
                  </div>
                ))}
                {assets.slice(0, Math.max(0, 5 - resources.length)).map((asset, index) => (
                  <div 
                    key={asset.id}
                    className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 border-2 border-white flex items-center justify-center overflow-hidden relative z-10"
                    style={{ zIndex: 5 - resources.length - index }}
                  >
                    {asset.asset?.image_url ? (
                      <img src={asset.asset.image_url} alt={asset.asset?.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold text-white">{getAssetInitials(asset)}</span>
                    )}
                  </div>
                ))}
                {totalCount > 5 && (
                  <div className="h-8 w-8 rounded-full bg-gray-300 border-2 border-white flex items-center justify-center text-xs font-medium text-gray-600">
                    +{totalCount - 5}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-500 italic">No resources assigned</span>
            )}
          </div>
          
          <ChevronDown className="h-4 w-4 text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors"
           onClick={() => setIsExpanded(false)}>
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Click to collapse</span>
          <ChevronUp className="h-4 w-4 text-gray-400" />
        </div>
      </div>
      
      <div className="p-4">
        <div className="space-y-2">
          {/* Team Resources */}
          {resources.map((resource) => {
            return (
              <div key={resource.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center overflow-hidden">
                    {getResourceAvatar(resource)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {resource.resource?.first_name} {resource.resource?.last_name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {resource.resource?.permission_role?.name || resource.role.charAt(0).toUpperCase() + resource.role.slice(1)}
                    </div>
                  </div>
                </div>
                {onRemoveResource && (
                  <button
                    onClick={() => onRemoveResource(resource.resource_id)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}

          {/* Assets */}
          {assets.map((bookingAsset) => {
            const asset = bookingAsset.asset;
            const initials = getAssetInitials(bookingAsset);
            return (
              <div key={bookingAsset.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center overflow-hidden">
                    {asset?.image_url ? (
                      <img src={asset.image_url} alt={asset?.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-xs font-bold text-white">{initials}</span>
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {asset?.name || 'Unknown Asset'}
                    </div>
                    <div className="text-xs text-orange-600">
                      {asset?.asset_type ? asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1) : 'Asset'}
                    </div>
                  </div>
                </div>
                {onRemoveAsset && (
                  <button
                    onClick={() => onRemoveAsset(bookingAsset.asset_id)}
                    className="text-xs font-medium text-red-600 hover:text-red-700 transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}

          {totalCount === 0 && (
            <div className="py-3 text-center">
              <p className="text-xs text-gray-500 italic">No resources assigned</p>
            </div>
          )}
        </div>
        
        {/* Add Resource Button */}
        {onAddResource && (
          <div className={`mt-3 ${onAddAsset ? '' : 'pt-3 border-t border-gray-200'}`}>
            <button
              onClick={onAddResource}
              className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-800 hover:border-gray-400 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Resource
            </button>
          </div>
        )}

        {/* Add Asset Button */}
        {onAddAsset && (
          <div className={`${onAddResource ? 'mt-2' : 'mt-3 pt-3 border-t border-gray-200'}`}>
            <button
              onClick={onAddAsset}
              className="w-full flex items-center justify-center px-3 py-2 border border-dashed border-orange-300 rounded-lg text-sm font-medium text-orange-600 hover:text-orange-800 hover:border-orange-400 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-300 transition-colors"
            >
              <Package className="h-4 w-4 mr-2" />
              Add Asset
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
